import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "context/auth-context";
import Pagination from "../Pagination/Pagination";
import { Alert } from "antd";
import { getAttemptSurvey, getMarkAsReadActive, getNotificationDetails } from "./Notifications.api";
import { Link } from "react-router-dom";
import { toastrError, toastrSuccess } from "lib/toastr";
import { FiArrowUpRight, FiBell, FiCheck, FiSearch } from "react-icons/fi";
import { v4 as uuidv4 } from "uuid";
import { axiosClient } from "api/api";
import { EmptyState, PageHeader, SkeletonRows } from "components/ui";

export const Notification = () => {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const [langCode, setLangCode] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [countryChangeMessage, setCountryChangeMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const queryClient = useQueryClient();
  const session_id: string = uuidv4();
  const getNotificationsData = useQuery(
    ["getNotifications", currentPage, langCode],
    () => {
      if (langCode) {
        return getNotificationDetails(currentPage, langCode, auth.panelistId);
      } else {
        return [];
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const perPage = 10;
  const startIndex = (currentPage - 1) * perPage;
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredData = getNotificationsData?.data?.data?.filter((item) =>
    item.study_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const mutation = useMutation(
    (studyName: any) => getMarkAsReadActive(studyName, auth?.panelistId),
    {
      onSuccess() {
        toastrSuccess("Read Successfully");
        queryClient.invalidateQueries(["getNotifications"]);
      },
      onError() {
        toastrError("Something went wrong");
      },
    }
  );

  const mutationAttempt = useMutation(
    (studyName: any) => getAttemptSurvey(studyName, auth?.panelistId),
    {
      onSuccess() {
        toastrSuccess("Fetch Successfully");
        queryClient.invalidateQueries(["getNotifications"]);
      },
      onError() {
        toastrError("Something went wrong");
      },
    }
  );

  const handleMarkAsRead = (studyName) => {
    mutation.mutate(studyName);
  };

  const handleAttempt = (studyName) => {
    mutationAttempt.mutate(studyName);
  };

  const getData = async () => {
    const ipUrl = `/api/user/get-ip-details`;
    const res = await axiosClient.get(ipUrl, { timeout: 5000, });
    if (res.data.data.country.currency.code == "USD") {
      setLangCode("En-US");
    } else if (res.data.data.country.currency.code == "CAD") {
      setLangCode("English-CA");
    } else if (res.data.data.country.currency.code == "GBP") {
      setLangCode("En-US");
    } else if (res.data.data.country.currency.code == "INR") {
      setLangCode("English-IN");
    } else {
      setLangCode(res.data.data.country_name + "-" + res.data.data.country.currency.code);
    }
    if (res && res.data) {
      if (!auth.user_country.includes(res.data.data.country_name)) {
        setCountryChangeMessage(
          <div>
            System identified you as <b>{res.data.data.country_name}</b> country user so{" "}
            <b>{res.data.data.country_name}</b> survey will be listed for you.
          </div>
        );
      }
    }
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <div className="zs-page">
      <PageHeader
        eyebrow="Inbox"
        title="Notifications"
        subtitle="Surveys matched to your profile. Attempt one now or mark it as read to clear it from your list."
      />

      {countryChangeMessage && (
        <div className="mb-4">
          <Alert message={countryChangeMessage} type="info" showIcon />
        </div>
      )}

      <section className="zs-card">
        <div className="zs-table-toolbar">
          <div className="min-w-0">
            <h2 className="zs-card-title">Survey alerts</h2>
            <p className="mt-0.5 zs-card-subtitle">Study details, length of interview and incidence rate.</p>
          </div>
          <div className="w-full zs-search sm:w-72">
            <FiSearch className="w-4 h-4" aria-hidden="true" />
            <input
              className="zs-input"
              type="text"
              name="search"
              aria-label={t("page.mysurvey.surveySearchPlaceholder")}
              placeholder={t("page.mysurvey.surveySearchPlaceholder")}
              onChange={handleSearch}
              value={searchQuery}
            />
          </div>
        </div>

        {getNotificationsData.isLoading ? (
          <SkeletonRows rows={6} />
        ) : (
          <div className="zs-table-wrap is-flush">
            <table className="zs-table">
              <thead>
                <tr>
                  <th scope="col" className="w-16">
                    S.NO
                  </th>
                  <th scope="col">STUDY NAME</th>
                  <th scope="col" className="is-num">
                    LOI
                  </th>
                  <th scope="col" className="is-num">
                    IR
                  </th>
                  <th scope="col" className="is-num">
                    CPI
                  </th>
                  <th scope="col">ACTION</th>
                  <th scope="col">SEEN</th>
                </tr>
              </thead>
              <tbody>
                {filteredData?.length ? (
                  filteredData?.map((item: any, key: number) => (
                    <tr key={key}>
                      <td className="tabular-nums text-ink-3">{startIndex + key + 1}</td>
                      <td className="is-strong max-w-[280px] truncate" title={item.study_name}>
                        {item.study_name}
                      </td>
                      <td className="is-num">{item.loi}</td>
                      <td className="is-num">{item.ir}</td>
                      <td className="is-num">{item.vendor_cpi}</td>
                      <td>
                        <Link
                          className="zs-btn zs-btn-primary zs-btn-sm"
                          onClick={() => {
                            handleAttempt(item.study_name),
                              window.open(
                                `https://zampparticipant.zamplia.com/?vid=313&SID=${item.s_id}&UID=${auth.panelistId}&cid=${item.api_client_id}&session_id=${session_id}&isMap=1`,
                                "_blank"
                              );
                          }}
                          to="#"
                        >
                          Attempt
                          <FiArrowUpRight aria-hidden="true" />
                        </Link>
                      </td>
                      <td>
                        <Link
                          className="zs-btn zs-btn-secondary zs-btn-sm"
                          to="#"
                          onClick={() => handleMarkAsRead(item.study_name)}
                        >
                          <FiCheck aria-hidden="true" />
                          Mark As Read
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="!p-0">
                      <EmptyState
                        icon={<FiBell />}
                        title="No Data Found"
                        description={searchQuery ? "No survey alerts match your search." : "You're all caught up. New survey alerts will appear here."}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {searchQuery.length == 0 && getNotificationsData?.data?.data?.length > 0 && (
          <div className="zs-table-footer">
            <span className="tabular-nums">
              Page {currentPage} of {getNotificationsData.data?.totalPages}
            </span>
            <Pagination
              setCurrentPage={setCurrentPage}
              totalPages={getNotificationsData.data?.totalPages}
              currentPage={currentPage}
            />
          </div>
        )}
      </section>
    </div>
  );
};
